import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../AuthContext";
import { useTheme } from "../../ThemeContext";
import { useSettings } from "../../SettingsContext";
import { authAPI } from "../../api";
import MIcon from "../../icons/MIcon";
import LanguageSwitcher from "./LanguageSwitcher";

/* ─── Navigation Items ───────────────────────────────────────────── */

// `labelKey` resolves against the i18n catalog at render time so nav labels
// follow the active language.
const adminNavItems = [
  { path: "/admin", labelKey: "nav.adminHome", icon: "desktop_windows" },
  {
    path: "/admin/organizations",
    labelKey: "nav.organizations",
    icon: "corporate_fare",
  },
  { path: "/admin/allowed-users", labelKey: "nav.allowedUsers", icon: "group_add" },
  { path: "/admin/settings", labelKey: "nav.settings", icon: "settings" },
];

/* ─── AppSidebar Component ───────────────────────────────────────── */

const AppSidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Seed from the last-known value so the Calendar nav item doesn't flicker
  // in after the async visibility fetch resolves on each navigation.
  const [calendarVisible, setCalendarVisible] = useState(
    () => localStorage.getItem("calendarVisible") === "true",
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { isPageEnabled } = useSettings();
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    authAPI
      .getCalendarVisibility()
      .then((res) => {
        setCalendarVisible(res.data.visible);
        localStorage.setItem(
          "calendarVisible",
          res.data.visible ? "true" : "false",
        );
      })
      .catch(() => {});
  }, []);

  // Feature-flag visibility: admins see everything; non-admins see only pages
  // enabled in Settings.
  const mainNavItems = [
    ...(isPageEnabled("agents", { isAdmin })
      ? [{ path: "/agents", labelKey: "nav.agents", icon: "smart_toy" }]
      : []),
    ...(calendarVisible && isPageEnabled("calendar", { isAdmin })
      ? [{ path: "/calendar", labelKey: "nav.calendar", icon: "calendar_month" }]
      : []),
    ...(isPageEnabled("analytics", { isAdmin })
      ? [{ path: "/analytics", labelKey: "nav.analytics", icon: "analytics" }]
      : []),
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const renderNavButton = (item) => {
    const active = isActive(item.path);
    return (
      <button
        key={item.path}
        onClick={() => handleNavigate(item.path)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200
          ${
            active
              ? "text-brand dark:text-brand border-r-2 border-brand dark:border-brand bg-brand/5 dark:bg-[#242424]"
              : "text-on-surface-light-variant dark:text-[#a0a0a0] hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-container dark:hover:bg-[#242424] border-r-2 border-transparent"
          }
        `}
      >
        <MIcon name={item.icon} filled={active} />
        <span>{t(item.labelKey)}</span>
      </button>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-screen">
      {/* Logo */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand dark:bg-brand flex items-center justify-center">
            <MIcon name="layers" className="text-white !text-[20px]" />
          </div>
          <div>
            <h2 className="font-display text-sm font-bold text-on-surface-light dark:text-on-surface tracking-tight uppercase">
              {t("common.appName")}
            </h2>
            <p className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant">
              {user?.is_admin ? t("nav.adminPanel") : t("nav.userPanel")}
            </p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-surface-light-highest dark:border-white/5" />

      {/* Navigation */}
      <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
        {mainNavItems.map(renderNavButton)}

        {/* Admin section — only visible to admins */}
        {user?.is_admin && (
          <>
            <div className="pt-4 pb-2 px-4">
              <p className="text-[11px] font-semibold text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider">
                {t("nav.administration")}
              </p>
            </div>
            {adminNavItems.map(renderNavButton)}
          </>
        )}
      </nav>

      {/* User info + Logout */}
      <div className="p-4 border-t border-surface-light-highest dark:border-white/5">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-brand/20 dark:bg-brand/20 flex items-center justify-center text-xs font-semibold text-brand dark:text-brand">
            {user?.user?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-on-surface-light dark:text-on-surface font-medium truncate">
              {user?.user}
            </p>
            <p className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant">
              {user?.is_admin ? t("nav.administrator") : t("nav.user")}
            </p>
          </div>
        </div>
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-on-surface-light-variant dark:text-[#a0a0a0] hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-container dark:hover:bg-[#242424] transition-all duration-200"
        >
          <MIcon name={isDark ? "light_mode" : "dark_mode"} />
          <span className="flex-1 text-left">
            {isDark ? t("nav.lightMode") : t("nav.darkMode")}
          </span>
          <div
            className={`
              w-9 h-5 rounded-full relative transition-colors duration-300
              ${
                isDark
                  ? "bg-brand/20 border border-brand/40"
                  : "bg-surface-light-highest border border-outline-light-variant"
              }
            `}
          >
            <div
              className={`
                absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all duration-300
                ${isDark ? "left-[18px] bg-brand" : "left-0.5 bg-outline-light"}
              `}
            />
          </div>
        </button>

        {/* Language switcher */}
        <LanguageSwitcher />

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-on-surface-light-variant dark:text-[#a0a0a0] hover:text-error-light dark:hover:text-error hover:bg-error-light/5 dark:hover:bg-error/5 transition-all duration-200"
        >
          <MIcon name="logout" />
          <span>{t("nav.logout")}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-lg bg-surface-light-lowest dark:bg-surface-container-high border border-surface-light-highest dark:border-white/10 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
      >
        <MIcon name={mobileOpen ? "close" : "menu"} className="lg" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 dark:bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          app-sidebar fixed top-0 left-0 h-full w-64 z-40
          transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:relative
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default AppSidebar;
